import React, { useState, useEffect, ChangeEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import MultiSelectDropdown from "../../../../components/common/MultiSelectDropdown";
import Dropdown from "../../../../components/common/Dropdown";
import FormGroup from "../../../../components/common/FormGroup";
import {
    updateScannableObject,
    getScanConfig,
    getScannableObjects,
    getScannableObjectTags,
    ScannableObjectTagResponse,
} from "../../../../api/progression";
import { getHoramaSpecies, HoramaSpecies, getHoramaSpeciesLabel } from "../../../../api/horama";
import { uploadFileToS3 } from "../../../../api/s3";
import i18n from "../../../../i18n";
import stepStyles from "../createStep.module.css";
import styles from "./CreateObject.module.css";
import { useNotification } from "../../../../context/NotificationContext";

interface ModifyObjectProps {
    navigateToNext: () => void;
}

const ModifyObject: React.FC<ModifyObjectProps> = ({ navigateToNext }) => {
    const { objectId } = useParams<{ objectId: string }>();
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const token = localStorage.getItem("token") || "";

    const [name, setName] = useState<string>("");
    const [position, setPosition] = useState<number>(1);
    const [referenceImageUrl, setReferenceImageUrl] = useState<string>("");
    const [recognitionKey, setRecognitionKey] = useState<string>("");
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [availableTags, setAvailableTags] = useState<ScannableObjectTagResponse[]>([]);
    const [imageRecognitionProvider, setImageRecognitionProvider] = useState<string>("OPENAI");
    const [horamaSpecies, setHoramaSpecies] = useState<HoramaSpecies[]>([]);
    const [horamaDefaultLanguage, setHoramaDefaultLanguage] = useState<string | null>(null);
    const [loadingSpecies, setLoadingSpecies] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);
    const [existingObjects, setExistingObjects] = useState<{ id: string; position: number }[]>([]);
    const [positionError, setPositionError] = useState<string>("");

    const linkId = trackData?.trackId || trackData?.id;

    useEffect(() => {
        const loadData = async () => {
            if (!trackData.trackId || !objectId) return;

            try {
                setLoadingData(true);
                const configResponse = await getScanConfig({ token, trackId: trackData.trackId });
                if (configResponse?.scanConfig) {
                    // Store the provider
                    setImageRecognitionProvider(configResponse.scanConfig.imageRecognitionProvider || "OPENAI");

                    // Load tags
                    const tagsResponse = await getScannableObjectTags({
                        token,
                        scanConfigId: configResponse.scanConfig.id,
                    });
                    if (tagsResponse?.scannableObjectTags) {
                        setAvailableTags(tagsResponse.scannableObjectTags);
                    }

                    // Load current object data and all objects for position validation
                    const objectsResponse = await getScannableObjects({
                        token,
                        scanConfigId: configResponse.scanConfig.id,
                    });

                    // Store all objects for position validation (excluding current object)
                    if (objectsResponse?.scannableObjects) {
                        setExistingObjects(
                            objectsResponse.scannableObjects
                                .filter((obj: any) => obj.id !== objectId)
                                .map((obj: any) => ({
                                    id: obj.id,
                                    position: obj.position,
                                })),
                        );
                    }

                    const currentObject = objectsResponse?.scannableObjects?.find((obj) => obj.id === objectId);
                    if (currentObject) {
                        // Use track language consistently for load and save
                        const langCode = trackData.language?.toUpperCase() || "EN";
                        const localized = currentObject.scannableObjectLocalized?.find(
                            (loc) => loc.language?.toUpperCase() === langCode,
                        );
                        setName(localized?.name || currentObject.scannableObjectLocalized?.[0]?.name || "");
                        setPosition(currentObject.position || 1);
                        setReferenceImageUrl(currentObject.referenceImageUrl || "");
                        setRecognitionKey(currentObject.recognitionKey || "");
                        setSelectedTags(currentObject.tags?.map((tag) => tag.id) || []);
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setLoadingData(false);
            }
        };

        loadData();
    }, [trackData.trackId, trackData.language, objectId, token]);

    // Load Horama species when provider is HORAMA
    useEffect(() => {
        const loadHoramaSpecies = async () => {
            if (imageRecognitionProvider !== "HORAMA") {
                setHoramaSpecies([]);
                setHoramaDefaultLanguage(null);
                return;
            }

            setLoadingSpecies(true);
            try {
                const response = await getHoramaSpecies({ token });
                if (response?.species) {
                    setHoramaSpecies(response.species);
                    setHoramaDefaultLanguage(response.defaultLanguage ?? null);
                }
            } catch (error) {
                console.error("Error loading Horama species:", error);
            } finally {
                setLoadingSpecies(false);
            }
        };

        loadHoramaSpecies();
    }, [imageRecognitionProvider, token]);

    const handleNameChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setName(e.target.value);
    };

    const handlePositionChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = parseInt(e.target.value) || 1;
        setPosition(value);

        // Check if position is already used by another object
        const conflictingObject = existingObjects.find((obj) => obj.position === value);
        if (conflictingObject) {
            setPositionError(t("positionAlreadyUsed"));
        } else {
            setPositionError("");
        }
    };

    const handleImageUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingImage(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setReferenceImageUrl(response.url);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRecognitionKeyChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRecognitionKey(e.target.value);
    };

    const handleSave = async () => {
        if (!objectId || !name) return;

        setLoading(true);
        try {
            // For OpenAI, auto-fill recognitionKey from name if not provided
            // For Horama, use the manually entered recognitionKey
            const finalRecognitionKey = imageRecognitionProvider === "HORAMA" ? recognitionKey : recognitionKey || name;

            const response = await updateScannableObject({
                token,
                scannableObjectId: objectId,
                name,
                position,
                referenceImageUrl: referenceImageUrl || undefined,
                language: trackData.language || "EN",
                tagIds: selectedTags,
                recognitionKey: finalRecognitionKey || undefined,
            });

            // Postgres saved but Weaviate not indexed: the object exists in the CMS and
            // will never be recognized in the app. Say so instead of navigating on.
            if (response?.weaviateWarning) {
                showNotification({
                    message: `${t("weaviateIndexIncomplete")} — ${response.weaviateWarning}`,
                    type: "warning",
                    duration: 20000,
                });
            }
            navigateToNext();
        } catch (error) {
            console.error("Error updating scannable object:", error);
            showNotification({
                message: error instanceof Error ? error.message : String(error),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`/create/tour/${linkId}/objects`);
    };

    const getTagLocalizedName = (tag: ScannableObjectTagResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = tag.scannableObjectTagLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || tag.scannableObjectTagLocalized?.[0]?.name || t("unnamed");
    };

    const tagOptions = availableTags
        .filter((tag) => tag.status)
        .map((tag) => ({
            value: tag.id,
            label: getTagLocalizedName(tag),
        }));

    // Get language name for display - use track language since that's what we're editing
    const languageName =
        trackData.language === "FR"
            ? t("french")
            : trackData.language === "EN"
              ? t("english")
              : trackData.language === "ES"
                ? t("spanish")
                : trackData.language === "PT"
                  ? t("portuguese")
                  : trackData.language === "DE"
                    ? t("german")
                    : trackData.language === "IT"
                      ? t("italian")
                      : trackData.language || "EN";

    if (loadingData) {
        return (
            <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${styles.root}`}>
                <p>{t("loading")}</p>
            </div>
        );
    }

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${styles.root}`}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/objects`}>{t("objects")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("modifyObject")}</span>
            </div>

            <TitleSubtitle title={t("modifyObject")} subtitle={t("updateObjectDetails")} layout="entityForm" />

            <div className={stepStyles.stepDescription}>
                {/* Name field */}
                <FormGroup
                    title={t("name")}
                    subtitle={t("objectNameDescription", { language: languageName })}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput placeholder={t("enterName")} value={name} onChange={handleNameChange} size="medium" />
                </FormGroup>

                {/* Position field */}
                <FormGroup title={t("position")} subtitle={t("objectPositionDescription")} required>
                    <TextInput
                        placeholder="1"
                        value={position.toString()}
                        onChange={handlePositionChange}
                        size="medium"
                        type="number"
                    />
                    {positionError && <p className={styles.errorText}>{positionError}</p>}
                </FormGroup>

                {/* Tag selection */}
                <FormGroup title={t("tag")} subtitle={t("objectTagDescription")}>
                    {tagOptions.length > 0 ? (
                        <MultiSelectDropdown
                            label={t("tag")}
                            options={tagOptions}
                            value={selectedTags}
                            onChange={setSelectedTags}
                            loading={false}
                            error={false}
                            disabled={false}
                            placeholder={t("selectTag")}
                        />
                    ) : (
                        <p className={styles.hintText}>{t("noTagsAvailable")}</p>
                    )}
                </FormGroup>

                {/* Reference image */}
                <FormGroup
                    title={t("referenceImage")}
                    subtitle={
                        imageRecognitionProvider === "WEAVIATE"
                            ? t("weaviateReferenceImageRequired")
                            : t("referenceImageDescription")
                    }
                    required={imageRecognitionProvider === "WEAVIATE"}
                >
                    {referenceImageUrl && (
                        <div style={{ marginBottom: "12px" }}>
                            <img src={referenceImageUrl} alt={t("referenceImage")} className={styles.previewImage} />
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handleImageUpload}
                        placeholder={uploadingImage ? t("uploading") : t("selectImage")}
                        disabled={uploadingImage}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                    />
                </FormGroup>

                {/* Recognition key for Horama - using species dropdown */}
                {imageRecognitionProvider === "HORAMA" && (
                    <FormGroup title={t("recognitionKey")} subtitle={t("horamaKeyDescription")} required>
                        {loadingSpecies ? (
                            <p className={styles.hintText}>{t("loadingSpecies")}</p>
                        ) : horamaSpecies.length > 0 ? (
                            <Dropdown
                                label=""
                                options={horamaSpecies.map((species) => ({
                                    value: species.id,
                                    label: getHoramaSpeciesLabel(species, i18n.language, horamaDefaultLanguage),
                                }))}
                                value={recognitionKey}
                                onChange={(value) => setRecognitionKey(value)}
                                placeholder={t("selectSpecies")}
                                loading={false}
                                error={false}
                            />
                        ) : (
                            <div>
                                <p className={styles.errorText}>{t("noHoramaSpeciesAvailable")}</p>
                                <TextInput
                                    placeholder={t("enterRecognitionKey")}
                                    value={recognitionKey}
                                    onChange={handleRecognitionKeyChange}
                                    size="medium"
                                />
                            </div>
                        )}
                    </FormGroup>
                )}
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("cancel")}
                    onClick={handleCancel}
                    variant="cancel"
                    shape="pill"
                    className={stepStyles.footerBtn}
                />
                <Button
                    label={loading ? t("saving") : t("save")}
                    onClick={handleSave}
                    disabled={
                        !name ||
                        loading ||
                        uploadingImage ||
                        (imageRecognitionProvider === "HORAMA" && !recognitionKey) ||
                        (imageRecognitionProvider === "WEAVIATE" && !referenceImageUrl) ||
                        !!positionError
                    }
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${stepStyles.footerBtn}`}
                />
            </div>
        </div>
    );
};

export default ModifyObject;
